import React from 'react';
import proptypes from 'prop-types';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { SIGN_IN } from '../constants/constants';
import { getAnalyticsEventName, logAnalytics, PAYPAL_EVENTS } from '../utils/AnalyticsHelper';
import {
  paypalMissingData,
  paypalSmartButtonAddToCartFailNewRelic,
  paypalSmartButtonCancelNewRelic,
  paypalSmartButtonClickNewRelic,
  paypalSmartButtonErrorNewRelic,
  paypalSmartButtonInitFailNewRelic,
  paypalSmartButtonSuccessNewRelic,
} from '../utils/NewRelicHelper';
import {
  addAlert, experienceType, getCartViewType, onApprove
} from '../utils/paypalUtils';
import { getTmxId, goToUrl } from '../utils/utils';

const { string, bool, func, ...PropTypes } = proptypes;

const PaypalSmartButton = (props) => {

  const {
    addToCart,
    CAACCheckbox,
    CAACExperience,
    cartRequest,
    checkoutType,
    customPaypalHandler,
    disabled,
    ffm,
    fromPage,
    hasSavedPaypal,
    initPaypal,
    isUpdatePaypal,
    itemPrice,
    orderId,
    payInFour,
    paypalButtonStyle,
    triggerGetCart,
    triggerSubmit
  } = props;

  const buttonStyle = {
    label: paypalButtonStyle,
    color: 'gold',
    shape: 'rect',
    borderRadius: 2,
    disableMaxHeight: true,
  };
  const isRegularPaypal = (fromPage === 'checkout');
  let returnUrl = '';
  let payPalInitOrderId = '';

  const onPaypalApprove = async (paypalData) => {
    paypalSmartButtonSuccessNewRelic(fromPage, orderId, payInFour, paypalData);
    if (isUpdatePaypal) {
      triggerGetCart({ checkoutType, orderId, paypalData });
      return true;
    }
    return onApprove(
      paypalData,
      orderId,
      isRegularPaypal,
      returnUrl,
      triggerSubmit,
      payPalInitOrderId
    );
  };

  const createPaypalOrder = async (cartId) => {
    const eventName = getAnalyticsEventName(fromPage, hasSavedPaypal, isRegularPaypal, payInFour);
    logAnalytics({ eventData: { cartRequest, cartViewType: getCartViewType(), itemPrice }, eventName });
    return new Promise((resolve, reject) => {
      initPaypal({
        variables: {
          requestContext: {
            cartId, // populate from variables or global
            experience: { component: fromPage, type: experienceType(checkoutType) }, // populate from variables or global
            expTypeFilter: { type: ffm },
            tmxProfileId: getTmxId(),
          }
        },
      }).then((resp) => {
        if (resp?.initPaypal) {
          returnUrl = resp?.initPaypal?.returnUrl;
          payPalInitOrderId = resp?.initPaypal?.cartId;
          resolve(resp?.initPaypal?.token);
        }
      }).catch((errorData) => {
        const error = errorData?.graphQLErrors?.[0]?.extensions?.cart_info_errors?.[0];
        if (error) {
          if (error?.errorDescription?.errorCode === 'CART_INFO_ERR_001') {
            paypalSmartButtonInitFailNewRelic(
              '', error?.errorDescription, payInFour);
            goToUrl(SIGN_IN + '?pp_persisted=true');
          } else {
            paypalSmartButtonInitFailNewRelic(error?.errorCode, error?.description, payInFour);
            LIFE_CYCLE_EVENT_BUS.trigger('paypal-checkout-button.initError');
            console.log('Paypal create order error: ' + errorData);
          }
        }
        addAlert(true);
        console.error('Paypal create order error: ' + errorData);
        reject(errorData);
      });
    });
  };

  const addTocart = (cartReq) => {
    logAnalytics({ eventData: cartReq, eventName: PAYPAL_EVENTS.CART_STASH });
    return addToCart({ cartRequest: cartReq, component: 'paypal' })
      .then((resp) => {
        if (resp?.data?.addToCart) {
          return resp?.data?.addToCart?.cartId;
        }
        if (resp?.errors) {
          paypalSmartButtonAddToCartFailNewRelic(
            fromPage,
            payInFour,
            resp?.errors?.[0]?.extensions.cart_info_errors?.[0]
          );
          LIFE_CYCLE_EVENT_BUS.trigger('paypal-checkout-button.addToCartError');
          return false;
        }
        return false;
      })
      .then((cartId) => {
        if (cartId) {
          return createPaypalOrder(cartId);
        }
        return false;
      })
      .catch((error) => {
        paypalSmartButtonAddToCartFailNewRelic(
          fromPage,
          payInFour,
          error?.errors?.[0]?.extensions.cart_info_errors?.[0]
        );
        LIFE_CYCLE_EVENT_BUS.trigger('paypal-checkout-button.addToCartError');
        console.log(error);
      });
  };

  const triggerCreateOrder = async () => {
    paypalSmartButtonClickNewRelic(ffm, fromPage, hasSavedPaypal, isUpdatePaypal, payInFour);

    let cartReq = cartRequest;
    if (customPaypalHandler) {
      cartReq = await customPaypalHandler();
    }

    if (cartReq) {
      const retVal = addTocart(cartReq);
      return retVal;
    }

    const fromCheckoutWithOrderId = (orderId && orderId !== '' && fromPage === 'checkout');

    if (fromCheckoutWithOrderId && (!CAACExperience || (CAACExperience && CAACCheckbox))) {
      const paypalToken = await createPaypalOrder(orderId);
      return paypalToken;
    }

    if (orderId && orderId !== '' && (fromPage === 'shoppingCart' || fromPage === 'addToCart')) {
      return createPaypalOrder(orderId);
    }

    const missingProps = fromPage === 'pip' ? 'cartRequest' : 'cartId';
    paypalMissingData(fromPage, missingProps, payInFour);
    return false;
  };

  const onCancel = (data) => {
    // This event hides the PayPal Button on PIP pages.
    LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', data);
    paypalSmartButtonCancelNewRelic(fromPage, orderId, payInFour);
  };

  const onError = (error) => {
    try {
      paypalSmartButtonErrorNewRelic(error, fromPage, payInFour, orderId);
    } catch (newRelicError) {
      console.log('PayPal New Relic logging error: ', newRelicError);
    }
  };

  return (
    <div style={{ display: 'grid', height: (hasSavedPaypal && payInFour) ? '40px' : '44px' }}>
      <PayPalButtons
        createOrder={triggerCreateOrder}
        disabled={disabled}
        forceReRender={[cartRequest]}
        fundingSource={hasSavedPaypal && payInFour ? 'paylater' : 'paypal'}
        onApprove={onPaypalApprove}
        onCancel={onCancel}
        onError={onError}
        style={buttonStyle}
      />
    </div>
  );
};

PaypalSmartButton.propTypes = {
  addToCart: func,
  CAACCheckbox: bool,
  CAACExperience: bool,
  // eslint-disable-next-line react/forbid-prop-types
  cartRequest: PropTypes.object,
  checkoutType: string,
  customPaypalHandler: func,
  disabled: bool,
  ffm: string.isRequired,
  fromPage: string.isRequired,
  hasSavedPaypal: bool,
  initPaypal: func.isRequired,
  isUpdatePaypal: bool,
  itemPrice: string,
  orderId: string,
  payInFour: bool,
  paypalButtonStyle: string,
  triggerGetCart: func,
  triggerSubmit: func.isRequired
};

PaypalSmartButton.defaultProps = {
  addToCart: () => { },
  CAACCheckbox: false,
  CAACExperience: false,
  cartRequest: {},
  checkoutType: '',
  customPaypalHandler: () => { },
  disabled: false,
  hasSavedPaypal: false,
  isUpdatePaypal: false,
  itemPrice: '',
  orderId: null,
  payInFour: false,
  paypalButtonStyle: 'paypal',
  triggerGetCart: () => { }
};

export { PaypalSmartButton };